import { Stack, Box, useMediaQuery, themes, Grid } from '@ace-clublabs/shared-component-library';
import { BenefitChartItem } from './BenefitChartItem';
import { setGlobalState, useGlobalState } from '../../state/state';
import { MembershipLevel, MSSProductStatus } from '@lib/types';
import { useHistory } from 'react-router-dom';
import { Pages } from '../../utils/constants';
import { useState } from 'react';
import { getIsBenefitLevelDowngradeAllowed, getIsBenefitLevelUpgradeAllowed } from '../../state/helpers';
import { MembershipProductType } from '@ace-clublabs/common-membership-api-types';
import { RVDowngradeModal } from './RVDowngradeModal';
import { useRef } from 'react';
import { UtagHelper } from '../../utils/UtagHelper';
import { promotionalLabelMap } from '../../utils/stringHelper';
import { MembershipBenefit } from './MembershipBenefit';
import { MakeChangeLink } from './MakeChangeLink';
import { ExploreBenefitButton } from './ExploreBenefitButton';
import { HowToMakeChangeModal } from './HowToMakeChangeModal';

export const BenefitCompareChart = () => {
  const history = useHistory();
  const [content] = useGlobalState('content');
  const [memberDetails] = useGlobalState('memberDetails');
  const [formData] = useGlobalState('formData');
  const requestedMembershipLevel = formData?.requestedMembershipLevel;
  const { benefitLevels, prospectiveMemberRules } = memberDetails;
  const { allowedMembershipLevels } = prospectiveMemberRules;
  const [showRVDowngradeModal, setShowRVDowngradeModal] = useState(false);
  const [selectedBenefitLevel, setSelectedBenefitLevel] = useState(null as null | MembershipLevel);
  const [showHowToMakeChangeModal, setShowHowToMakeChangeModal] = useState(false);
  const howToUpgrade: string = 'How to upgrade';
  const howToCancelDowngrade: string = 'How to cancel/downgrade';
  const additionalBenefits = content.additionalBenefits || [];

  const currentLevel = benefitLevels?.existingLevel as MembershipLevel;

  const hasRV = formData?.products?.find(
    (product) => product.type === MembershipProductType.RV && product.status !== MSSProductStatus.REMOVED
  );

  const getRVProductRule = (benefitLevel: MembershipLevel) => {
    const rv = memberDetails.prospectiveMemberRules?.memberProducts?.find(
      (product) => product.productType === MembershipProductType.RV
    );
    const rvProductRules = rv?.productRules?.find(
      (product) => product.membershipLevel.toLowerCase() === benefitLevel.toLowerCase()
    );
    return rvProductRules?.rule;
  };

  const canHaveRV = (benefitLevel: MembershipLevel) => {
    return getRVProductRule(benefitLevel) !== MSSProductStatus.NOT_ALLOWED;
  };

  const updateBenefitLevel = (requestedBenefitLevel: MembershipLevel) => {
    const updatedFormData = { ...formData, requestedMembershipLevel: requestedBenefitLevel };

    if (!canHaveRV(requestedBenefitLevel) && hasRV) {
      let products = [...formData.products!];
      // TODO verify what happens when it is include
      if ([MSSProductStatus.ADDED, MSSProductStatus.INCLUDED].includes(hasRV.status)) {
        products = products.filter((product) => product.type !== MembershipProductType.RV);
      } else if (hasRV.status === MSSProductStatus.EXISTING) {
        products = products.map((product) =>
          product.type === MembershipProductType.RV ? { ...product, status: MSSProductStatus.REMOVED } : product
        );
      }

      updatedFormData.products = products;
    }

    const rvIsIncluded = getRVProductRule(requestedBenefitLevel) === MSSProductStatus.INCLUDED;
    if (rvIsIncluded) {
      let products = (updatedFormData.products || []).filter((product) => product.type !== MembershipProductType.RV);

      products.push({
        type: MembershipProductType.RV,
        status: MSSProductStatus.INCLUDED,
      });

      updatedFormData.products = products;
    }

    setGlobalState('formData', updatedFormData);

    const ctaTag = () => {
      const benefitLevelHierarchy = [MembershipLevel.CLASSIC, MembershipLevel.PLUS, MembershipLevel.PREMIER];
      const currentLevelIdx = benefitLevelHierarchy.indexOf(currentLevel);
      const newLevelIdx = benefitLevelHierarchy.indexOf(requestedBenefitLevel);

      if (currentLevelIdx === newLevelIdx) {
        return 'keepcurrentbenefits';
      } else if (newLevelIdx > currentLevelIdx) {
        return 'upgrade';
      } else {
        return 'downgrade';
      }
    };

    history.push({
      pathname: Pages.ManageMembershipPage,
      search: UtagHelper.getIntcmpQueryParam(Pages.ChangeBenefitLevelPage, ctaTag()),
    });
  };

  const selectBenefitOnClick = (benefitLevel: MembershipLevel) => {
    if (!canHaveRV(benefitLevel) && hasRV) {
      setShowRVDowngradeModal(true);
      setSelectedBenefitLevel(benefitLevel);
    } else {
      updateBenefitLevel(benefitLevel);
    }
  };

  const isUpgradeAllowed = getIsBenefitLevelUpgradeAllowed(memberDetails);
  const isDowngradeAllowed = getIsBenefitLevelDowngradeAllowed(memberDetails);

  const isAllowedLevel = (membershipLevel: MembershipLevel) => allowedMembershipLevels.includes(membershipLevel);

  enum BenefitSubtitles {
    CLASSIC = 'Essential benefits for the road and beyond',
    PLUS = 'All the benefits of Classic, and:',
    PREMIER = 'All the benefits of Classic and Plus, and: ',
  }

  interface IGetButtonPropsResponse {
    [MembershipLevel.CLASSIC]: { title: string; isButton: boolean; buttonVariant?: string };
    [MembershipLevel.PLUS]: { title: string; isButton: boolean; buttonVariant?: string };
    [MembershipLevel.PREMIER]: { title: string; isButton: boolean; buttonVariant?: string };
  }

  const BUTTON_PROPS = {
    keepCurrentLevel: { title: 'Keep current benefits', isButton: true, buttonVariant: 'outlined' },
    upgradeAllowed: { title: 'Upgrade', isButton: true, buttonVariant: 'contained' },
    upgradeNotAllowed: { title: howToUpgrade, isButton: false },
    downgradeAllowed: { title: 'Downgrade', isButton: true, buttonVariant: 'outlined' },
    downgradeNotAllowed: { title: howToCancelDowngrade, isButton: false },
    currentRenew: { title: 'Renew with current benefits', isButton: true, buttonVariant: 'contained' },
    differentRenew: { title: 'Renew', isButton: true, buttonVariant: 'contained' },
  };

  const getButtonProps = (): IGetButtonPropsResponse | undefined => {
    const isInRenewal = memberDetails?.inRenewal === true;

    if (isInRenewal) {
      return {
        [MembershipLevel.CLASSIC]:
          currentLevel === MembershipLevel.CLASSIC ? BUTTON_PROPS.currentRenew : BUTTON_PROPS.differentRenew,
        [MembershipLevel.PLUS]:
          currentLevel === MembershipLevel.PLUS ? BUTTON_PROPS.currentRenew : BUTTON_PROPS.differentRenew,
        [MembershipLevel.PREMIER]:
          currentLevel === MembershipLevel.PREMIER ? BUTTON_PROPS.currentRenew : BUTTON_PROPS.differentRenew,
      };
    }

    switch (true) {
      case currentLevel === MembershipLevel.CLASSIC && isUpgradeAllowed:
        return {
          [MembershipLevel.CLASSIC]: BUTTON_PROPS.keepCurrentLevel,
          [MembershipLevel.PLUS]: isAllowedLevel(MembershipLevel.PLUS)
            ? BUTTON_PROPS.upgradeAllowed
            : BUTTON_PROPS.upgradeNotAllowed,
          [MembershipLevel.PREMIER]: isAllowedLevel(MembershipLevel.PREMIER)
            ? BUTTON_PROPS.upgradeAllowed
            : BUTTON_PROPS.upgradeNotAllowed,
        };
      case currentLevel === MembershipLevel.CLASSIC && !isUpgradeAllowed:
        return {
          [MembershipLevel.CLASSIC]: BUTTON_PROPS.keepCurrentLevel,
          [MembershipLevel.PLUS]: BUTTON_PROPS.upgradeNotAllowed,
          [MembershipLevel.PREMIER]: BUTTON_PROPS.upgradeNotAllowed,
        };
      case currentLevel === MembershipLevel.PLUS && isUpgradeAllowed && isDowngradeAllowed:
        return {
          [MembershipLevel.CLASSIC]: BUTTON_PROPS.downgradeAllowed,
          [MembershipLevel.PLUS]: BUTTON_PROPS.keepCurrentLevel,
          [MembershipLevel.PREMIER]: BUTTON_PROPS.upgradeAllowed,
        };
      case currentLevel === MembershipLevel.PLUS && !isUpgradeAllowed && isDowngradeAllowed:
        return {
          [MembershipLevel.CLASSIC]: BUTTON_PROPS.downgradeAllowed,
          [MembershipLevel.PLUS]: BUTTON_PROPS.keepCurrentLevel,
          [MembershipLevel.PREMIER]: BUTTON_PROPS.upgradeNotAllowed,
        };
      case currentLevel === MembershipLevel.PLUS && isUpgradeAllowed && !isDowngradeAllowed:
        return {
          [MembershipLevel.CLASSIC]: BUTTON_PROPS.downgradeNotAllowed,
          [MembershipLevel.PLUS]: BUTTON_PROPS.keepCurrentLevel,
          [MembershipLevel.PREMIER]: BUTTON_PROPS.upgradeAllowed,
        };
      case currentLevel === MembershipLevel.PLUS && !isUpgradeAllowed && !isDowngradeAllowed:
        return {
          [MembershipLevel.CLASSIC]: BUTTON_PROPS.downgradeNotAllowed,
          [MembershipLevel.PLUS]: BUTTON_PROPS.keepCurrentLevel,
          [MembershipLevel.PREMIER]: BUTTON_PROPS.upgradeNotAllowed,
        };
      case currentLevel === MembershipLevel.PREMIER && isDowngradeAllowed:
        return {
          [MembershipLevel.CLASSIC]: isAllowedLevel(MembershipLevel.CLASSIC)
            ? BUTTON_PROPS.downgradeAllowed
            : BUTTON_PROPS.downgradeNotAllowed,
          [MembershipLevel.PLUS]: isAllowedLevel(MembershipLevel.PLUS)
            ? BUTTON_PROPS.downgradeAllowed
            : BUTTON_PROPS.downgradeNotAllowed,
          [MembershipLevel.PREMIER]: BUTTON_PROPS.keepCurrentLevel,
        };
      case currentLevel === MembershipLevel.PREMIER && !isDowngradeAllowed:
        return {
          [MembershipLevel.CLASSIC]: BUTTON_PROPS.downgradeNotAllowed,
          [MembershipLevel.PLUS]: BUTTON_PROPS.downgradeNotAllowed,
          [MembershipLevel.PREMIER]: BUTTON_PROPS.keepCurrentLevel,
        };
      default:
        return undefined;
    }
  };

  const benefitLevelMapToButtonProps = getButtonProps() || {
    [MembershipLevel.CLASSIC]: BUTTON_PROPS.keepCurrentLevel,
    [MembershipLevel.PLUS]: BUTTON_PROPS.keepCurrentLevel,
    [MembershipLevel.PREMIER]: BUTTON_PROPS.keepCurrentLevel,
  };

  const cardRef = useRef(null);
  const fallbackProps = BUTTON_PROPS.keepCurrentLevel;

  const isDesktop = useMediaQuery(themes.light.breakpoints.up('lg'));

  const { buttonVariant, isButton, title } =
    benefitLevelMapToButtonProps[requestedMembershipLevel as MembershipLevel] || fallbackProps;

  const BenefitCompareChartMobile = (
    <>
      <Stack
        ref={cardRef}
        direction="column"
        className="benefit-compare-container"
        gap="0.25rem"
        maxWidth="545px"
        width="100%"
      >
        <Stack direction="row" gap="0.25rem" sx={{ flexWrap: 'wrap', alignItems: 'stretch' }}>
          {additionalBenefits.map(({ membershipLevel: benefitLevel }, i: number) => {
            return (
              <Box
                key={benefitLevel}
                sx={{
                  marginTop: '2rem',
                  flex: '1 1 0',
                  minWidth: 0,
                  '@media (max-width: 359px)': {
                    flex: '1 1 100%',
                    minWidth: '100%',
                    marginTop: '0.75rem',
                  },
                }}
              >
                <BenefitChartItem
                  benefitTitle={benefitLevel as MembershipLevel}
                  promotionalLabel={promotionalLabelMap[benefitLevel as MembershipLevel]}
                  selected={benefitLevel === (requestedMembershipLevel as MembershipLevel)}
                  className={`${benefitLevel}-benefit-chart`}
                  benefitOnClick={() => {
                    setGlobalState('formData', {
                      ...formData,
                      requestedMembershipLevel: benefitLevel as MembershipLevel,
                    });
                  }}
                />
              </Box>
            );
          })}
        </Stack>
        <MembershipBenefit
          className={`${requestedMembershipLevel}-benefit-chart`}
          buttonTitle={title}
          buttonVariant={buttonVariant}
          buttonOrLinkOnClick={() => selectBenefitOnClick(requestedMembershipLevel as MembershipLevel)}
          subtitle={BenefitSubtitles[requestedMembershipLevel as keyof typeof BenefitSubtitles]}
          additionalBenefits={additionalBenefits.find(
            ({ membershipLevel: level }) => level === requestedMembershipLevel
          )}
          isButton={isButton}
        />
        <Box marginTop="1.5rem">
          <MakeChangeLink onOpenModal={() => setShowHowToMakeChangeModal(true)} />
        </Box>
      </Stack>

      <RVDowngradeModal
        visible={showRVDowngradeModal}
        onCancel={() => setShowRVDowngradeModal(false)}
        onConfirm={() => updateBenefitLevel(selectedBenefitLevel!)}
      />
    </>
  );

  const BenefitCompareChartDesktop = (
    <Grid container columnSpacing={'1rem'} columns={12} direction="row" margin="2rem 0" alignItems="stretch">
      <Stack ref={cardRef} direction="column" className="benefit-compare-container" maxWidth="lg" width="100%">
        <Box alignSelf="flex-end">
          <MakeChangeLink onOpenModal={() => setShowHowToMakeChangeModal(true)} />
        </Box>
        <Stack direction="row" gap="1rem" sx={{ flexWrap: 'wrap', alignItems: 'stretch' }}>
          {additionalBenefits.map(({ membershipLevel: benefitLevel }, i: number) => {
            const { buttonVariant, isButton, title } =
              benefitLevelMapToButtonProps[benefitLevel as MembershipLevel] || fallbackProps;
            return (
              <Grid key={benefitLevel} size={4} offset={0} display="flex">
                <BenefitChartItem
                  benefitTitle={benefitLevel as MembershipLevel}
                  promotionalLabel={promotionalLabelMap[benefitLevel as MembershipLevel]}
                  selected={benefitLevel === (requestedMembershipLevel as MembershipLevel)}
                  className={`${benefitLevel}-benefit-chart`}
                  isDesktop={isDesktop}
                >
                  <MembershipBenefit
                    className={`${benefitLevel}-benefit-chart`}
                    buttonTitle={title}
                    buttonVariant={buttonVariant}
                    buttonOrLinkOnClick={() => selectBenefitOnClick(benefitLevel as MembershipLevel)}
                    subtitle={BenefitSubtitles[benefitLevel as keyof typeof BenefitSubtitles]}
                    additionalBenefits={additionalBenefits.find(({ membershipLevel: level }) => level === benefitLevel)}
                    isButton={isButton}
                  />
                </BenefitChartItem>
              </Grid>
            );
          })}
        </Stack>
        <ExploreBenefitButton />
      </Stack>

      <RVDowngradeModal
        visible={showRVDowngradeModal}
        onCancel={() => setShowRVDowngradeModal(false)}
        onConfirm={() => updateBenefitLevel(selectedBenefitLevel!)}
      />
    </Grid>
  );

  return (
    <>
      {isDesktop ? BenefitCompareChartDesktop : BenefitCompareChartMobile}
      <HowToMakeChangeModal
        showHowToMakeChangeModal={showHowToMakeChangeModal}
        closeModal={() => setShowHowToMakeChangeModal(false)}
      />
    </>
  );
};
